import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Subject,
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  merge,
  of,
  startWith,
  switchMap,
  tap,
  throttleTime,
  throwError
} from 'rxjs';

import { BalanceService, CategoriesService } from 'core';
import { KorviuButtonComponent, KorviuLoaderComponent } from 'shared';
import { IFeaturedReward } from 'shared/types/featured-reward.interface';
import { RefreshOnLanguageChangeDirective } from 'shared/directives/refresh-on-language-change.directive';

@Component({
  selector: 'app-featured-rewards',
  imports: [TranslateModule, KorviuButtonComponent, KorviuLoaderComponent, RouterLink],
  templateUrl: './featured-rewards.component.html',
  styleUrl: './featured-rewards.component.scss',
  hostDirectives: [RefreshOnLanguageChangeDirective]
})
export class FeaturedRewardsComponent implements OnInit {
  readonly #router = inject(Router);
  readonly balanceService = inject(BalanceService);
  readonly categoriesService = inject(CategoriesService);
  readonly destroyRef = inject(DestroyRef);
  readonly #translateService = inject(TranslateService);

  readonly #favoriteClick$ = new Subject<IFeaturedReward>();
  readonly #reloadFeatured$ = new Subject<void>();

  readonly language = toSignal(this.#translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this.#translateService.currentLang || this.#translateService.defaultLang || 'es'
  });
  readonly featured = signal<IFeaturedReward[]>([]);
  readonly localizedFeatured = computed(() => {
    const isSpanish = this.language() === 'es';

    return this.featured().map(product => ({
      ...product,
      displayName: isSpanish ? product.name_es?.trim() || product.name : product.name
    }));
  });
  readonly remainingFeatured = computed(() => this.localizedFeatured().slice(1));
  readonly featuredIsLoading = signal<boolean>(false);

  get lessThan500Ganas() {
    return this.balanceService.spendableGanas() < 500;
  }

  constructor() {
    this.#favoriteClick$
      .pipe(
        throttleTime(1000),
        switchMap(product => {
          const previousFavoriteValue = !!product.is_favorite;
          this.#setFeaturedFavorite(product.id, !previousFavoriteValue);

          return this.categoriesService.toggleFavoriteProduct(product.id).pipe(
            catchError(error => {
              this.#setFeaturedFavorite(product.id, previousFavoriteValue);

              return throwError(() => error);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnInit(): void {
    merge(
      this.#reloadFeatured$,
      this.#translateService.onLangChange.pipe(
        map(event => event.lang),
        startWith(this.#translateService.currentLang || this.#translateService.defaultLang || 'es'),
        distinctUntilChanged()
      )
    )
      .pipe(
        switchMap(() => {
          this.featured.set([]);
          this.featuredIsLoading.set(true);

          return this.categoriesService.getFeaturedRewards({ pageSize: 4, page: 1 }).pipe(
            tap(res => this.featured.set(res.results)),
            catchError(() => of(null)),
            finalize(() => this.featuredIsLoading.set(false))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  toggleFavorite(event: MouseEvent, product: IFeaturedReward): void {
    event.preventDefault();
    event.stopPropagation();
    this.#favoriteClick$.next(product);
  }

  onViewAllClick(): void {
    void this.#router.navigate(['/workspace/rewards/featured']);
  }

  navigateToProduct(productId: number): void {
    this.#router.navigate(['workspace', 'rewards', 'product', productId]);
  }

  getFeatured() {
    this.#reloadFeatured$.next();
  }

  #setFeaturedFavorite(productId: number, isFavorite: boolean): void {
    this.featured.update(products =>
      products.map(product => (product.id === productId ? { ...product, is_favorite: isFavorite } : product))
    );
  }
}
